import { useMemo } from 'react';

import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useHeaderButtonOptions } from '../../../../../components/Header';
import { defaultHeaderLeft } from '../../../../components/DefaultHeaderLeft';
import {
  DEFAULT_SCREEN_OPTIONS,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useGiftCardsTabStackOptions = () => {
  const { currentBreakpoint } = useResponsive();
  const titles = useRouteTitles();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const { headerLeftOptions } = useHeaderButtonOptions({ headerLeft: defaultHeaderLeft });

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(
    () => ({
      ...DEFAULT_SCREEN_OPTIONS,
      ...SCREEN_HEADER_OPTIONS,
      ...headerLeftOptions,
      headerShown: currentBreakpoint.isXS,
    }),
    [currentBreakpoint.isXS, headerLeftOptions],
  );

  /// ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Tabs ─────────────────────────────────────────────────

      Home: {
        title: titles.giftCardsTabTitle,
        customDocumentTitle: titles.giftCardsTabCustomDocumentTitle,
        headerShown: false,
        navbarLayout: 'static-with-border',
      },
      Checkout: {
        title: titles.giftCardCheckoutTitle,
        customDocumentTitle: titles.giftCardCheckoutCustomDocumentTitle,
        navbarLayout: 'static-with-border',
      },
      Confirmation: {
        title: titles.giftCardConfirmationTitle,
        customDocumentTitle: titles.giftCardConfirmationCustomDocumentTitle,
      },
      Redeem: {
        title: titles.redeemGiftCardTitle,
        customDocumentTitle: titles.giftCardRedemptionCustomDocumentTitle,
        headerShown: !isNavbarV2Enabled && currentBreakpoint.isXS,
        navbarLayout: 'static-with-border',
      },
    } satisfies ScreenOptionsMap;
  }, [
    stackOptions,
    titles.giftCardCheckoutTitle,
    titles.giftCardCheckoutCustomDocumentTitle,
    titles.giftCardConfirmationTitle,
    titles.giftCardConfirmationCustomDocumentTitle,
    titles.giftCardsTabTitle,
    titles.giftCardsTabCustomDocumentTitle,
    titles.redeemGiftCardTitle,
    titles.giftCardRedemptionCustomDocumentTitle,
    currentBreakpoint.isXS,
    isNavbarV2Enabled,
  ]);
};
